import React, { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { MaskedLine, FadeUp } from "@/components/MaskedReveal";

const EASE = [0.76, 0, 0.24, 1];
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const expertiseAreas = [
  {
    n: "01",
    title: "Air Ambulance & Medical Transport",
    body:
      "Comprehensive operating procedures, crew training programs, and mission profiles that support safe and efficient aeromedical operations in demanding environments.",
  },
  {
    n: "02",
    title: "Intelligence, Surveillance & Reconnaissance",
    body:
      "ISR flight operations, crew coordination, mission planning, aircraft integration, and operational procedures for surveillance and reconnaissance missions.",
  },
  {
    n: "03",
    title: "Wildfire Detection & FLIR Operations",
    body:
      "Aerial wildfire detection and monitoring programs utilizing Forward Looking Infrared (FLIR) technology — mission planning, crew training, procedures, aircraft integration.",
  },
  {
    n: "04",
    title: "GPS Mapping & Aerial Survey",
    body:
      "Standardized procedures for precision GPS mapping, aerial photography, environmental surveys, infrastructure inspection, and specialized aerial data collection.",
  },
  {
    n: "05",
    title: "Aircraft Modification Flight Test Support",
    body:
      "Experienced flight crews for post-maintenance and post-modification functional check flights, systems evaluation, acceptance testing, and operational validation.",
  },
];

const services = [
  "Aviation program planning and implementation",
  "Standard Operating Procedure (SOP) development",
  "Flight Operations Manual development",
  "Crew qualification standards",
  "Initial and recurrent training program development",
  "Instructor standardization",
  "Crew Resource Management (CRM)",
  "Risk management systems",
  "Operational safety procedures",
  "Mission-specific flight profiles",
  "Aircraft acceptance and operational integration",
  "Flight department consulting",
  "Operational readiness assessments",
];

const aircraft = [
  "Beechcraft King Air Series",
  "Cessna 408 SkyCourier",
  "Cessna 208 Caravan",
  "Citation CJ4",
  "Additional turbine aircraft upon request",
];

const missionTypes = [
  "Air Ambulance / Medical Transport",
  "ISR (Surveillance & Reconnaissance)",
  "Wildfire Detection / FLIR",
  "GPS Mapping / Aerial Survey",
  "Aircraft Modification Flight Test",
  "New Flight Department Development",
  "Existing Program Enhancement",
  "Other",
];

export default function GovernmentPrograms() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const heroTextY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);

  const [form, setForm] = useState({
    agency: "",
    contact_name: "",
    email: "",
    phone: "",
    mission_type: missionTypes[0],
    aircraft_type: "",
    timeline: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (
      !form.agency.trim() ||
      !form.contact_name.trim() ||
      !form.email.trim() ||
      !form.message.trim()
    ) {
      toast.error("Please complete agency, contact name, email and briefing.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/government-briefing`, form);
      setSent(true);
      toast.success("Briefing received. Our program team will respond directly.");
      setForm({
        agency: "",
        contact_name: "",
        email: "",
        phone: "",
        mission_type: missionTypes[0],
        aircraft_type: "",
        timeline: "",
        message: "",
      });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong.";
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Nav />
      <main className="bg-[#050505] text-white">
        {/* HERO */}
        <section
          ref={ref}
          className="relative h-[92svh] w-full overflow-hidden"
          data-testid="government-hero"
        >
          <motion.div style={{ y }} className="absolute inset-0 w-full h-[130%] -top-[15%]">
            <img
              src="https://customer-assets-wrfwihn1.emergentagent.net/job_elyon-aviation/artifacts/zob4fftm_IMG_7911.webp"
              alt="Government aviation program — King Air with firefighting support"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 hero-vignette" />
          </motion.div>

          <motion.div
            style={{ y: heroTextY }}
            className="relative z-10 h-full flex flex-col justify-between mx-auto max-w-[1920px] px-6 md:px-12 lg:px-16 pt-32 pb-14"
          >
            <div className="flex items-start justify-between">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9, duration: 1, ease: EASE }}
                className="font-mono-label text-white/60"
              >
                <span className="text-[#FF5A00]">◉</span> Programs · Government &amp; Special Missions
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.1, duration: 1, ease: EASE }}
                className="hidden md:block font-mono-label text-white/60 text-right"
              >
                Mission Focused
                <br />
                <span className="text-[#FF5A00]">Safety Driven · Operationally Proven</span>
              </motion.div>
            </div>

            <div className="flex-1 flex flex-col justify-end">
              <div className="font-mono-label text-white/60 mb-6">
                Aviation Program Development
              </div>
              <h1 className="font-display uppercase leading-[0.86] tracking-[-0.04em] text-[13vw] md:text-[8.5vw]">
                <MaskedLine delay={0.4} animate={{ y: "0%" }}>
                  Government &amp;
                </MaskedLine>
                <MaskedLine delay={0.55} animate={{ y: "0%" }}>
                  <span className="font-serif-italic italic pr-4 text-[0.85em]">
                    Special
                  </span>
                  {"\u00A0"}Missions
                </MaskedLine>
                <MaskedLine delay={0.7} animate={{ y: "0%" }} className="pl-[6vw] md:pl-[12vw]">
                  Aviation
                </MaskedLine>
              </h1>

              <motion.p
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.4, duration: 1, ease: EASE }}
                className="mt-8 md:mt-12 max-w-2xl text-white/70 text-base md:text-lg leading-relaxed"
              >
                Building aviation programs that are mission ready. El Elyon Air
                partners with government agencies, military organizations,
                humanitarian operators, and commercial aviation departments to
                develop safe, standardized, and effective aviation programs
                tailored to each mission.
              </motion.p>
            </div>

            <div className="flex items-end justify-between font-mono-label text-white/50 text-[10px]">
              <Link
                to="/"
                className="hover:text-[#FF5A00] transition-colors duration-500"
                data-testid="gov-back-home"
              >
                ← El Elyon Air · Home
              </Link>
              <div className="hidden md:block">Scroll — Program Detail</div>
              <div>PROG-GOV-24</div>
            </div>
          </motion.div>
        </section>

        {/* INTRO */}
        <section className="py-24 md:py-40 border-t border-white/10">
          <div className="mx-auto max-w-[1920px] px-6 md:px-12 lg:px-16 grid grid-cols-1 md:grid-cols-12 gap-8">
            <div className="md:col-span-5">
              <FadeUp>
                <div className="font-mono-label text-white/50 mb-6">
                  <span className="text-[#FF5A00]">◉</span> Approach · 001
                </div>
              </FadeUp>
              <h2 className="font-display text-4xl md:text-6xl uppercase leading-[0.9] tracking-tight">
                <MaskedLine>Practical</MaskedLine>
                <MaskedLine delay={0.1}>
                  <span className="font-serif-italic italic text-white/70">
                    expertise.
                  </span>
                </MaskedLine>
              </h2>
            </div>
            <div className="md:col-span-6 md:col-start-7 space-y-6">
              <FadeUp>
                <p className="text-white/70 text-base md:text-lg leading-relaxed">
                  Whether establishing a new flight department or enhancing an
                  existing operation, we provide practical expertise built on
                  real-world operational experience. Our programs are designed
                  to improve safety, operational efficiency, crew proficiency,
                  and long-term mission success.
                </p>
              </FadeUp>
            </div>
          </div>
        </section>

        {/* EXPERTISE AREAS */}
        <section className="py-24 md:py-40 border-t border-white/10">
          <div className="mx-auto max-w-[1920px] px-6 md:px-12 lg:px-16">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16 md:mb-24">
              <div className="md:col-span-6">
                <FadeUp>
                  <div className="font-mono-label text-white/50 mb-6">
                    <span className="text-[#FF5A00]">◉</span> Expertise · Mission Sets
                  </div>
                </FadeUp>
                <h2 className="font-display text-5xl md:text-7xl uppercase leading-[0.9] tracking-tight">
                  <MaskedLine>Our areas</MaskedLine>
                  <MaskedLine delay={0.12}>of expertise.</MaskedLine>
                </h2>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {expertiseAreas.map((area, i) => (
                <motion.div
                  key={area.n}
                  initial={{ opacity: 0, y: 60 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0, margin: "0px 0px -10% 0px" }}
                  transition={{ duration: 1, ease: EASE, delay: (i % 3) * 0.08 }}
                  className="border border-white/10 bg-[#0A0A0C] p-6 md:p-8 min-h-[280px] flex flex-col"
                  data-testid={`expertise-area-${area.n}`}
                >
                  <span className="font-serif-italic text-white/40 text-4xl md:text-5xl leading-none">
                    {area.n}
                  </span>
                  <h3 className="mt-8 font-display text-xl md:text-2xl uppercase tracking-tight leading-tight">
                    {area.title}
                  </h3>
                  <p className="mt-4 text-white/65 text-sm md:text-[15px] leading-relaxed">
                    {area.body}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* SERVICES LIST */}
        <section className="py-24 md:py-40 border-t border-white/10 bg-[#0A0A0C]">
          <div className="mx-auto max-w-[1920px] px-6 md:px-12 lg:px-16">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16 md:mb-20">
              <div className="md:col-span-6">
                <FadeUp>
                  <div className="font-mono-label text-white/50 mb-6">
                    <span className="text-[#FF5A00]">◉</span> Services · Program Development
                  </div>
                </FadeUp>
                <h2 className="font-display text-5xl md:text-7xl uppercase leading-[0.9] tracking-tight">
                  <MaskedLine>Program development</MaskedLine>
                  <MaskedLine delay={0.12}>services.</MaskedLine>
                </h2>
              </div>
              <div className="md:col-span-5 md:col-start-8 flex items-end">
                <FadeUp delay={0.15}>
                  <p className="text-white/60 text-base md:text-lg leading-relaxed">
                    Every element of a working aviation program — architected,
                    documented, trained, and audited to standard.
                  </p>
                </FadeUp>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4 border-t border-white/10 pt-10">
              {services.map((s, i) => (
                <motion.div
                  key={s}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0, margin: "0px 0px -10% 0px" }}
                  transition={{ duration: 0.8, ease: EASE, delay: (i % 3) * 0.05 }}
                  className="flex items-start gap-4 py-3 border-b border-white/10"
                  data-testid={`gov-service-${i}`}
                >
                  <span className="font-mono-label text-[#FF5A00] mt-1 min-w-[24px]">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="text-white/80 text-sm md:text-[15px] leading-relaxed">
                    {s}
                  </span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* IN THE FIELD - real engagement photos */}
        <section
          className="py-24 md:py-40 border-t border-white/10"
          data-testid="gov-in-the-field"
        >
          <div className="mx-auto max-w-[1920px] px-6 md:px-12 lg:px-16">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-14">
              <div className="md:col-span-6">
                <FadeUp>
                  <div className="font-mono-label text-white/50 mb-6">
                    <span className="text-[#FF5A00]">◉</span> In the Field · Real Engagements
                  </div>
                </FadeUp>
                <h2 className="font-display text-4xl md:text-6xl uppercase leading-[0.9] tracking-tight">
                  <MaskedLine>Programs</MaskedLine>
                  <MaskedLine delay={0.12}>
                    <span className="font-serif-italic italic text-white/70">
                      in motion.
                    </span>
                  </MaskedLine>
                </h2>
              </div>
              <div className="md:col-span-5 md:col-start-8 flex items-end">
                <FadeUp delay={0.2}>
                  <p className="text-white/60 text-base md:text-lg leading-relaxed max-w-md">
                    Moments from real client engagements — briefings, wing
                    ceremonies, and international team hand-offs. Every mission
                    starts as a conversation and ends as a standard.
                  </p>
                </FadeUp>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
              {[
                {
                  src: "https://customer-assets-wrfwihn1.emergentagent.net/job_elyon-aviation/artifacts/ih9zfaqu_IMG_2816.JPG",
                  label: "Program briefing · client desk",
                  col: "md:col-span-8",
                  ratio: "aspect-[16/9]",
                },
                {
                  src: "https://customer-assets-wrfwihn1.emergentagent.net/job_elyon-aviation/artifacts/rljjsebk_c50d37af-384f-42a3-a852-0a8a6c8000ef.JPG",
                  label: "Wing ceremony · type qualification",
                  col: "md:col-span-4",
                  ratio: "aspect-[4/5]",
                },
                {
                  src: "https://customer-assets-wrfwihn1.emergentagent.net/job_elyon-aviation/artifacts/409dna5q_c02a044a-1d09-44c4-b2c7-9ef13be7edbf.JPG",
                  label: "International program team · hangar",
                  col: "md:col-span-12",
                  ratio: "aspect-[21/9]",
                },
              ].map((p, i) => (
                <motion.figure
                  key={i}
                  initial={{ opacity: 0, y: 60 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0, margin: "0px 0px -10% 0px" }}
                  transition={{ duration: 1.1, ease: EASE, delay: (i % 2) * 0.1 }}
                  className={`relative ${p.col} col-span-1`}
                  data-testid={`gov-field-shot-${i}`}
                >
                  <div
                    className={`relative corner-frame overflow-hidden spotlight-img ${p.ratio} w-full`}
                  >
                    <img
                      src={p.src}
                      alt={p.label}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1500ms] ease-out hover:scale-105"
                    />
                  </div>
                  <figcaption className="mt-4 flex items-center justify-between font-mono-label text-white/50">
                    <span>{String(i + 1).padStart(2, "0")}</span>
                    <span className="text-white/60">{p.label}</span>
                  </figcaption>
                </motion.figure>
              ))}
            </div>
          </div>
        </section>

        {/* AIRCRAFT EXPERIENCE */}
        <section className="py-24 md:py-40 border-t border-white/10">
          <div className="mx-auto max-w-[1920px] px-6 md:px-12 lg:px-16 grid grid-cols-1 md:grid-cols-12 gap-8">
            <div className="md:col-span-5">
              <FadeUp>
                <div className="font-mono-label text-white/50 mb-6">
                  <span className="text-[#FF5A00]">◉</span> Aircraft · Experience
                </div>
              </FadeUp>
              <h2 className="font-display text-4xl md:text-6xl uppercase leading-[0.9] tracking-tight">
                <MaskedLine>Types we</MaskedLine>
                <MaskedLine delay={0.12}>
                  <span className="font-serif-italic italic text-white/70">
                    know cold.
                  </span>
                </MaskedLine>
              </h2>
            </div>
            <div className="md:col-span-6 md:col-start-7 space-y-4">
              {aircraft.map((a, i) => (
                <motion.div
                  key={a}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0, margin: "0px 0px -10% 0px" }}
                  transition={{ duration: 0.9, ease: EASE, delay: i * 0.08 }}
                  className="flex items-center gap-6 border-b border-white/10 pb-4"
                  data-testid={`gov-aircraft-${i}`}
                >
                  <span className="font-serif-italic text-white/40 text-3xl md:text-4xl min-w-[64px]">
                    0{i + 1}
                  </span>
                  <span className="font-display text-xl md:text-2xl uppercase tracking-tight">
                    {a}
                  </span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* TRUSTED BY */}
        <section
          className="py-20 md:py-28 border-t border-white/10 bg-[#050505]"
          data-testid="gov-trusted-by"
        >
          <div className="mx-auto max-w-[1920px] px-6 md:px-12 lg:px-16">
            <FadeUp>
              <div className="font-mono-label text-white/50 mb-10 text-center">
                <span className="text-[#FF5A00]">◉</span> Trusted By · Operational Experience Alongside
              </div>
            </FadeUp>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 md:gap-8 items-stretch">
              {[
                { name: "MAG Aerospace", short: "MAG", domain: "magaero.com", color: "#0A2540" },
                { name: "Textron", short: "Textron", domain: "textron.com", color: "#003DA5" },
                { name: "3RGI", short: "3RGI", domain: "3rgi.com", color: "#1F1F1F" },
                { name: "Raytheon", short: "RTX", domain: "rtx.com", color: "#D50032" },
                { name: "Leidos", short: "Leidos", domain: "leidos.com", color: "#00B140" },
                { name: "Tenax", short: "Tenax", domain: "tenaxaerospace.com", color: "#0E2A47" },
              ].map((c, i) => (
                <motion.div
                  key={c.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0, margin: "0px 0px -10% 0px" }}
                  transition={{ duration: 0.9, ease: EASE, delay: i * 0.06 }}
                  className="group relative flex flex-col border border-white/10 bg-[#0A0A0C] hover:border-[#FF5A00]/50 hover:bg-white/[0.03] transition-all duration-500 overflow-hidden"
                  data-testid={`trusted-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {/* Brand plate — light background so favicon reads properly */}
                  <div className="relative flex items-center justify-center h-24 bg-white/95 group-hover:bg-white transition-colors duration-500">
                    <img
                      src={`https://www.google.com/s2/favicons?domain=${c.domain}&sz=128`}
                      alt={`${c.name} mark`}
                      className="h-10 w-10 object-contain"
                      loading="lazy"
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                    <span
                      className="ml-3 font-brand text-lg leading-none tracking-tight"
                      style={{ color: c.color }}
                    >
                      {c.short}
                    </span>
                  </div>
                  {/* Label footer */}
                  <div className="flex items-center justify-between px-4 py-3 border-t border-white/10">
                    <span className="font-mono-label text-white/30 text-[9px]">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="font-mono-label text-white/70 text-[10px] tracking-[0.24em]">
                      {c.name}
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* COLLABORATIVE APPROACH */}
        <section className="py-24 md:py-40 border-t border-white/10 bg-[#0A0A0C]">
          <div className="mx-auto max-w-[1920px] px-6 md:px-12 lg:px-16 grid grid-cols-1 md:grid-cols-12 gap-8">
            <div className="md:col-span-4">
              <FadeUp>
                <div className="font-mono-label text-white/50 mb-6">
                  <span className="text-[#FF5A00]">◉</span> Approach · Collaborative
                </div>
              </FadeUp>
              <h2 className="font-display text-4xl md:text-5xl uppercase leading-[0.9] tracking-tight">
                <MaskedLine>Every</MaskedLine>
                <MaskedLine delay={0.12}>mission</MaskedLine>
                <MaskedLine delay={0.24}>is unique.</MaskedLine>
              </h2>
            </div>
            <div className="md:col-span-7 md:col-start-6 space-y-6">
              <FadeUp>
                <p className="text-white/75 text-base md:text-lg leading-relaxed">
                  Every mission presents unique operational requirements. We work
                  closely with our clients to understand their objectives,
                  operational environment, regulatory framework, and mission
                  profile before developing customized solutions that support
                  long-term success.
                </p>
              </FadeUp>
              <FadeUp delay={0.1}>
                <p className="text-white/70 text-base md:text-lg leading-relaxed">
                  Whether your organization is launching a new aviation
                  capability, expanding an existing fleet, or seeking to improve
                  operational effectiveness, El Elyon Air delivers practical,
                  scalable solutions backed by professional aviation experience.
                </p>
              </FadeUp>
              <FadeUp delay={0.2}>
                <p className="font-serif-italic text-[#FF5A00] text-2xl md:text-3xl leading-relaxed pt-4">
                  Mission Focused. Safety Driven. Operationally Proven.
                </p>
              </FadeUp>
            </div>
          </div>
        </section>

        {/* BRIEFING FORM */}
        <section
          id="briefing"
          className="py-24 md:py-40 border-t border-white/10"
          data-testid="gov-briefing-section"
        >
          <div className="mx-auto max-w-[1920px] px-6 md:px-12 lg:px-16">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12">
              <div className="md:col-span-5">
                <div className="font-mono-label text-white/50 mb-6">
                  <span className="text-[#FF5A00]">◉</span> Contact · Program Briefing
                </div>
                <h2 className="font-display uppercase leading-[0.84] tracking-tighter text-[14vw] md:text-[8vw]">
                  <MaskedLine>Request</MaskedLine>
                  <MaskedLine delay={0.12}>a briefing.</MaskedLine>
                </h2>
                <FadeUp delay={0.2}>
                  <p className="mt-10 text-white/60 max-w-md text-base md:text-lg leading-relaxed">
                    Share the outline of your program — agency, mission type,
                    aircraft, timeline — and our program team will respond
                    directly to schedule a confidential briefing.
                  </p>
                </FadeUp>
                <div className="mt-14 space-y-6 font-mono-label text-white/60">
                  <div>
                    <div className="text-white/40 mb-1">Program Ops</div>
                    <div className="text-white text-[13px] tracking-[0.24em]">
                      programs@elelyonair.com
                    </div>
                  </div>
                  <div>
                    <div className="text-white/40 mb-1">Positioning</div>
                    <div className="text-white text-[13px] tracking-[0.24em]">
                      Mission Focused · Safety Driven · Operationally Proven
                    </div>
                  </div>
                </div>
              </div>

              <form
                onSubmit={submit}
                className="md:col-span-6 md:col-start-7 border border-white/10 bg-[#0A0A0C] p-6 md:p-10"
                data-testid="gov-briefing-form"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Field label="Agency / Organization" name="agency" value={form.agency} onChange={handleChange} testid="gov-input-agency" required />
                  <Field label="Contact Name" name="contact_name" value={form.contact_name} onChange={handleChange} testid="gov-input-name" required />
                  <Field label="Email" name="email" type="email" value={form.email} onChange={handleChange} testid="gov-input-email" required />
                  <Field label="Phone (optional)" name="phone" value={form.phone} onChange={handleChange} testid="gov-input-phone" />
                  <div className="flex flex-col gap-2">
                    <label className="font-mono-label text-white/50">Mission Type</label>
                    <select
                      name="mission_type"
                      value={form.mission_type}
                      onChange={handleChange}
                      className="bg-transparent border-b border-white/20 focus:border-[#FF5A00] py-3 text-white text-sm transition-colors duration-500"
                      data-testid="gov-input-mission-type"
                    >
                      {missionTypes.map((m) => (
                        <option key={m} value={m} className="bg-[#0A0A0C] text-white">
                          {m}
                        </option>
                      ))}
                    </select>
                  </div>
                  <Field label="Aircraft Type" name="aircraft_type" value={form.aircraft_type} onChange={handleChange} testid="gov-input-aircraft" />
                  <div className="md:col-span-2">
                    <Field label="Timeline (optional)" name="timeline" value={form.timeline} onChange={handleChange} testid="gov-input-timeline" placeholder="e.g. Q3 stand-up, ongoing recurrent" />
                  </div>
                </div>

                <div className="mt-6 flex flex-col gap-2">
                  <label className="font-mono-label text-white/50">Program Briefing</label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    rows={6}
                    placeholder="Outline your objectives, operational environment, regulatory framework, and mission profile."
                    className="bg-transparent border-b border-white/20 focus:border-[#FF5A00] py-3 text-white text-sm placeholder:text-white/25 resize-none transition-colors duration-500"
                    data-testid="gov-input-message"
                    required
                  />
                </div>

                <motion.button
                  type="submit"
                  disabled={loading}
                  whileTap={{ scale: 0.98 }}
                  className="btn-outline btn-solid mt-10 w-full justify-center disabled:opacity-60"
                  data-testid="gov-submit"
                >
                  {loading ? "Transmitting…" : sent ? "Sent — Send Another?" : "Request Briefing"}
                  <span aria-hidden>→</span>
                </motion.button>
              </form>
            </div>
          </div>
        </section>

        {/* BOTTOM CTA STRIP */}
        <section className="py-16 md:py-24 border-t border-white/10 bg-[#050505]">
          <div className="mx-auto max-w-[1920px] px-6 md:px-12 lg:px-16 flex flex-col md:flex-row items-start md:items-end justify-between gap-8">
            <div className="font-display uppercase text-3xl md:text-5xl leading-[0.9] tracking-tight max-w-2xl">
              Contact El Elyon Air to discuss how we can help develop or enhance your aviation program.
            </div>
            <div className="flex gap-4">
              <Link to="/#contact" className="btn-outline btn-solid" data-testid="gov-cta-contact">
                General Contact
                <span aria-hidden>→</span>
              </Link>
              <Link to="/" className="btn-outline" data-testid="gov-cta-home">
                <span aria-hidden>←</span>
                El Elyon Air · Home
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}

const Field = ({ label, name, value, onChange, type = "text", required, testid, placeholder }) => (
  <div className="flex flex-col gap-2">
    <label className="font-mono-label text-white/50">{label}</label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      placeholder={placeholder}
      autoComplete="off"
      data-testid={testid}
      className="bg-transparent border-b border-white/20 focus:border-[#FF5A00] py-3 text-white text-sm placeholder:text-white/25 transition-colors duration-500"
    />
  </div>
);
